import * as Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import HighchartsBoost from "highcharts/modules/boost";
import HighchartsExporting from "highcharts/modules/exporting";
import HighchartsHeatmap from "highcharts/modules/heatmap";
import { FC, useEffect, useRef, useState } from "react";
import { HeatmapModel } from "../models/heatmap.model";
import "./chartStyle.scss";
import Exporting from "highcharts/modules/exporting";
import OfflineExporting from "highcharts/modules/offline-exporting";

HighchartsBoost(Highcharts);
HighchartsExporting(Highcharts);
HighchartsHeatmap(Highcharts);
Exporting(Highcharts);
OfflineExporting(Highcharts);

interface HeatmapHighChartComponentProps {
  chartTitle: string;
  seriesData: HeatmapModel;
  tickPositions: string[];
  extraData?: any;
  redirectRoute?: string;
  colorMinMax?: any;
  xAxisTitle: string;
  yAxisTitle: string;
  chartType?: string;
  motionDataLoaded?: any;
  setSelectedLocation?: any;
  setActiveKey?: any;
  fileName?: string;
  chartName?: string;
}

interface PointClickConfig {
  chartType?: string;
  setSelectedLocation?: any;
  setActiveKey?: any;
  extraData?: any;
  redirectRoute?: string;
}

function makePointClickHandler(config: PointClickConfig) {
  const { chartType, setSelectedLocation, setActiveKey, extraData, redirectRoute } = config;

  return function (this: Highcharts.Point, event: any) {
    const xIndex = event.point.x;
    const xLabel = this.series.xAxis.categories[event.point.x];
    const location = xLabel.split("_")[0];

    if (chartType && chartType != "Sensor" && setSelectedLocation) {
      setSelectedLocation(location);
    }

    if (setActiveKey) setActiveKey("Sensor");

    if (extraData?.length > 0 && extraData[xIndex - 1]) {
      const code = extraData[xIndex - 1].code;
      if (code && redirectRoute) {
        const route = redirectRoute + `${btoa(code)}`;
        const newTab = window.open(`${route}`, "_blank");
        newTab?.focus();
      }
    } else if (redirectRoute) {
      const route = redirectRoute + `${xLabel}`;
      const newTab = window.open(`${route}`, "_blank");
      newTab?.focus();
    }
  };
}

interface HeatmapTooltipConfig {
  seriesData: HeatmapModel;
  extraData?: any;
  chartType?: string;
  tickPositions: string[];
}

function makeHeatmapTooltipFormatter(config: HeatmapTooltipConfig) {
  const { seriesData, extraData, chartType, tickPositions } = config;

  return function (this: Highcharts.TooltipFormatterContextObject): string {
    // const dataIndex = (this.point as any)?.index;
    // const dataIndex = customData?.index;
    // debugger;
    const stringKey = seriesData?.identicalStringsList[this.point.x - 1];
    // const location = seriesData?.locationList
    //   ? seriesData?.locationList[dataIndex]
    //   : "";

    // Get extraData value for current point using the same logic as click event
    const xLabel = this.series.xAxis.categories[this.point.x];
    const extraDataValue =
      extraData?.length > 0
        ? extraData?.find((data: any) => data?.displayName === xLabel)
        : null;
    const level = extraDataValue?.level || "";

    const hour = this.point.y;
    const hourValue = hour || 0;
    const value = this.point.value;
    const chartTitleHtml = `<div style="text-align: center;"><b>${chartType}: </b>${stringKey}</div><br />`;
    const pointInfoHtml = `<b>Time:</b> ${tickPositions[hourValue] || hour
      } hrs<br /><b>Motion Count:</b> ${value ?? "No Data"}`;

    /*           const locationName = location
      ? `<br /><b>Location:</b> ${location}`
      : ""; */
    const levelName = level ? `<br /><b>Level:</b> ${level}<br />` : "";

    return chartTitleHtml + pointInfoHtml + levelName;
  };
}

const HeatmapHighChartComponent: FC<HeatmapHighChartComponentProps> = ({
  chartTitle,
  seriesData,
  extraData,
  redirectRoute,
  tickPositions,
  colorMinMax,
  xAxisTitle,
  yAxisTitle,
  chartType,
  motionDataLoaded,
  setSelectedLocation,
  setActiveKey,
  fileName,
  chartName,
}) => {
  const formattedData = seriesData?.newData?.map((point, index) => [
    point[0],
    point[1],
    point[2],
    index,
  ]);

  const chartComponentRef = useRef<HighchartsReact.RefObject>(null);

  const [customHighChartOptions, setCustomHighChartOptions] = useState({});
  const cellHeight = 21;

  useEffect(() => {
    if (!seriesData) return;

    const options: Highcharts.Options = {
      chart: {
        type: "heatmap",
        style: {
          fontFamily: '"Cabin", Helvetica, Arial, sans-serif',
        },
        zooming: {
          type: "x",
        },
        height: chartType === "Sensor" ? tickPositions.length * cellHeight : "",
      },

      plotOptions: {
        heatmap: {
          point: {
            events: {
              click: makePointClickHandler({
                chartType,
                setSelectedLocation,
                setActiveKey,
                extraData,
                redirectRoute,
              }),
            },
          },
          borderWidth: 0.2,
          borderColor: "#ddd",
        },
      },

      boost: {
        useGPUTranslations: true,
        seriesThreshold: 1,
        // Chart-level boost when there are more than 5 series in the chart
      },

      credits: {
        enabled: false,
      },

      title: {
        text: "",
      },

      tooltip: {
        formatter: makeHeatmapTooltipFormatter({
          seriesData,
          extraData,
          chartType,
          tickPositions,
        }),
      },

      xAxis: {
        categories: ["", ...seriesData?.identicalStringsList || []],
        min: 1,
        max: seriesData?.identicalStringsList?.length,
        title: {
          text: xAxisTitle || "Sensor Node",
          style: {
            fontSize: "16px",
            fontWeight: "bold",
            textAlign: "right",
          },
        },

        showLastLabel: true,
        tickLength: 16,
      },

      yAxis: {
        title: {
          text: yAxisTitle || "Time(hours)",
          style: {
            fontSize: "16px",
            fontWeight: "bold",
          },
        },
        labels: {
          y: 4, // Fine-tune vertical alignment to center with ticks
          step: 1,
          align: "right",
          x: -10, // Horizontal offset to align with ticks
          style: {
            textOverflow: "none",
          },
        },
        minPadding: 0,
        maxPadding: 0,
        startOnTick: true,
        endOnTick: true,
        categories: tickPositions,
        tickWidth: 1,
        tickLength: 5,
        lineWidth: 1,
        tickPosition: "outside",
        tickmarkPlacement: "on",
        gridLineWidth: 0,
      },

      colorAxis: {
        stops: [
          [0, "#99ccff"],
          [0.0001, "#66b2ff"], // 0 - light medium blue
          [0.33, "#3385cc"], // mid blue
          [0.66, "#0A2F80"], // Catalina Blue
          [1, "#082160"], // cool black https://www.schemecolor.com/bounce-with-blues.php
        ],
        min: colorMinMax?.min || 0,
        max: xAxisTitle == "Sensors" ? 200 : colorMinMax?.max || 1000,
      },
      legend: {
        x: 40, // This is added for perfectly aligning legend with y-axis title
        y: 0,
      },
      series: [
        {
          type: "heatmap",
          name: "Heatmap",
          data: formattedData,
          turboThreshold: 0,
          boostThreshold: 0,
        },
      ],
      accessibility: {
        enabled: false,
      },
      exporting: {
        enabled: true,
        fallbackToExportServer: false,
        filename: fileName || "chart",
        sourceWidth: 1200, // Increase source dimensions
        sourceHeight: 500,
        scale: 1, // Maintain original scale
        chartOptions: chartName
          ? {
            chart: {
              height: "100%", // Use relative height
              width: "100%", // Use relative width
              spacingTop: 30, // Add more space at the top for the title
              marginTop: 100, // Increase the top margin to push chart content down
            },
            title: {
              text: chartName, // Dynamic title from prop
              align: "center",
              margin: 20,
              style: {
                fontSize: "24px",
                fontWeight: "bold",
              },
            },
          }
          : {
            chart: {
              height: "100%", // Use relative height
              width: "100%", // Use relative width
            },
            title: {
              style: {
                fontSize: "14px",
              },
            },
          },
        buttons: {
          contextButton: {
            menuItems: [
              "viewFullscreen",
              "printChart",
              "separator",
              "downloadPNG",
              "downloadJPEG",
              "downloadPDF",
              "downloadSVG",
            ],
          },
        },
      },
    };

    setCustomHighChartOptions(options);
  }, [seriesData]);

  return (
    <div>
      {chartTitle ? (
        <h4
          className="h4"
          style={{
            marginLeft: "1rem",
            zIndex: 999,
            position: "relative",
          }}
        >
          {chartTitle}
        </h4>
      ) : null}

      {customHighChartOptions && seriesData?.identicalStringsList?.length ? (
        <HighchartsReact
          // key={chartCount}
          highcharts={Highcharts}
          options={customHighChartOptions}
          ref={chartComponentRef}
        />
      ) : (
        <div
          style={{
            height: "400px",
            width: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: "#D9D9D9",
            fontSize: "22px",
            fontWeight: "600",
            color: "#6d6d6d",
          }}
        >
          No Data Available
        </div>
      )}
    </div>
  );
};

export default HeatmapHighChartComponent;
